"use client"

import { styled, withStyles } from "@mui/styles"
import { Component } from "react"
import { injectIntl } from "react-intl"
import { connect } from "react-redux"
import { bindActionCreators } from "redux"
import { Styles } from "src/styles/material-styles"
import * as faqActions from "src/redux/actions/faqs"
import PropTypes from "prop-types"
import { AbilityContext } from "src/config/ability-context"
import Api from "src/services/api"
import { CircularProgress, Container, Grid, IconButton, TextField, Button, InputAdornment } from "@mui/material"
import Header from "src/components/header-external"
import { PapiroConsole } from "src/utils/papiroConsole"
import { Search, ArrowUpward, ArrowDownward } from "@mui/icons-material"
import { Overlay } from "src/styles/global"
import CustomizedSnackbars from "src/components/material-snackbars"
import LogoMarca from "src/images/new-logo2-cmtech.png"
import Footer from "src/components/footer-external"
import Pagination from "@mui/material/Pagination";
import { FormattedMessage } from "react-intl";
import * as articleActions from 'src/redux/actions/articles';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Card,
    CardContent,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    Chip,
    Divider,
} from "@mui/material"
import {
    ExpandMore,
    PictureAsPdf,
    Description,
    Image,
    VideoFile,
    AudioFile,
    AttachFile,
    OpenInNew,
} from "@mui/icons-material"
import { getFileDownloadLink } from "../../services/attachment.service"


const TAG_COLOR_PALETTE = [
    {
        name: "blue",
        backgroundColor: "#eff6ff",
        color: "#1e40af",
        border: "#dbeafe",
    },
    {
        name: "emerald",
        backgroundColor: "#ecfdf5",
        color: "#059669",
        border: "#d1fae5",
    },
    {
        name: "amber",
        backgroundColor: "#fffbeb",
        color: "#d97706",
        border: "#fed7aa",
    },
    {
        name: "rose",
        backgroundColor: "#fff1f2",
        color: "#e11d48",
        border: "#fecdd3",
    },
    {
        name: "violet",
        backgroundColor: "#f5f3ff",
        color: "#7c3aed",
        border: "#ddd6fe",
    },
    {
        name: "cyan",
        backgroundColor: "#ecfeff",
        color: "#0891b2",
        border: "#cffafe",
    },
    {
        name: "orange",
        backgroundColor: "#fff7ed",
        color: "#ea580c",
        border: "#fed7aa",
    },
    {
        name: "teal",
        backgroundColor: "#f0fdfa",
        color: "#0d9488",
        border: "#ccfbf1",
    },
]


const StyledTextField = styled(TextField)({
    backgroundColor: "#fff",
    borderRadius: "12px",
    "& .MuiOutlinedInput-root": {
        borderRadius: "12px",
        "& fieldset": {
            border: "1px solid #e5e7eb",
        },
        "&:hover fieldset": {
            border: "1px solid #d1d5db",
        },
        "&.Mui-focused fieldset": {
            border: "2px solid #6366f1",
        },
    },
    "& .MuiInputBase-input": {
        padding: "12px 16px",
        fontSize: "0.95rem",
    },
})

const StyledFormControl = styled(FormControl)({
    "& .MuiOutlinedInput-root": {
        backgroundColor: "#fff",
        borderRadius: "8px",
        "& fieldset": {
            border: "1px solid #e5e7eb",
        },
        "&:hover fieldset": {
            border: "1px solid #d1d5db",
        },
        "&.Mui-focused fieldset": {
            border: "2px solid #6366f1",
        },
    },
})

const SearchButton = styled(Button)({
    backgroundColor: "#6366f1",
    color: "#fff",
    borderRadius: "8px",
    padding: "12px 24px",
    "&:hover": {
        backgroundColor: "#5856eb",
    },
    "&:disabled": {
        backgroundColor: "#d1d5db",
        color: "#9ca3af",
    },
})

const StyledAccordion = styled(Accordion)(({ highlighted }) => ({
    backgroundColor: highlighted ? "#f0f9ff" : "#fff",
    boxShadow: "none",

    borderRadius: "0px",
    margin: "0px",
    "&:first-of-type": {
        borderTopLeftRadius: "8px",
        borderTopRightRadius: "8px",
    },
    "&:last-of-type": {
        borderBottomLeftRadius: "8px",
        borderBottomRightRadius: "8px",
    },
    "&:not(:last-of-type)": {
        borderBottom: "none",
    },
    "&:before": {
        display: "none",
    },
    "&.Mui-expanded": {
        margin: "0",
    },
}))

const StyledAccordionSummary = styled(AccordionSummary)({
    backgroundColor: "#fff",
    padding: "16px 24px",
    minHeight: "auto",
    "&:hover": {
        backgroundColor: "#f9fafb",
    },
    "&.Mui-expanded": {
        backgroundColor: "#f3f4f6",
        borderBottom: "1px solid #e5e7eb",
    },
    "& .MuiAccordionSummary-content": {
        margin: "8px 0",
    },
    "& .MuiAccordionSummary-expandIconWrapper": {
        color: "#6366f1",
    },
})

const StyledAccordionDetails = styled(AccordionDetails)({
    padding: "24px",
    backgroundColor: "#fff",
    borderTop: "none",
})

const MainCard = styled(Card)({
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
    border: "1px solid #e5e7eb",
})

const StyledChip = styled(Chip)(({ theme, colorvariant }) => ({
    margin: "2px 4px 2px 0",
    fontSize: "0.75rem",
    height: "24px",
    borderRadius: "12px",
    ...(colorvariant === "primary" && {
        backgroundColor: "#e0e7ff",
        color: "#3730a3",
        border: "1px solid #c7d2fe",
    }),
    ...(colorvariant === "secondary" && {
        backgroundColor: "#f0fdf4",
        color: "#166534",
        border: "1px solid #bbf7d0",
    }),
    ...(colorvariant === "warning" && {
        backgroundColor: "#fef3c7",
        color: "#92400e",
        border: "1px solid #fde68a",
    }),
    ...(colorvariant === "info" && {
        backgroundColor: "#e0f2fe",
        color: "#0c4a6e",
        border: "1px solid #bae6fd",
    }),
}))

const AttachmentItem = styled(Box)({
    display: "flex",
    alignItems: "center",
    padding: "8px 12px",
    margin: "4px 8px 4px 0",
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
        backgroundColor: "#f1f5f9",
        borderColor: "#cbd5e1",
        transform: "translateY(-1px)",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    },
})

class AttachmentIcon extends Component {
    getFileExtension(filename) {
        return filename.split(".").pop().toLowerCase()
    }

    getIconByExtension(extension) {
        switch (extension) {
            case "pdf":
                return <PictureAsPdf sx={{ color: "#dc2626" }} />
            case "doc":
            case "docx":
                return <Description sx={{ color: "#2563eb" }} />
            case "xls":
            case "xlsx":
                return <Description sx={{ color: "#059669" }} />
            case "jpg":
            case "jpeg":
            case "png":
            case "gif":
            case "bmp":
            case "svg":
                return <Image sx={{ color: "#059669" }} />
            case "mp4":
            case "avi":
            case "mov":
            case "wmv":
            case "flv":
                return <VideoFile sx={{ color: "#7c3aed" }} />
            case "mp3":
            case "wav":
            case "flac":
            case "aac":
                return <AudioFile sx={{ color: "#ea580c" }} />
            default:
                return <AttachFile sx={{ color: "#6b7280" }} />
        }
    }

    render() {
        const { filename } = this.props
        const extension = this.getFileExtension(filename)
        return this.getIconByExtension(extension)
    }
}

class FaqExternal extends Component {
    constructor(props) {
        super(props)
        const generalSearchFaqId = localStorage.getItem("generalSearchFaqId")
        localStorage.removeItem("generalSearchFaqId")
        this.state = {
            clientportal: {},
            loading: false,
            openNotification: false,
            notificationVariant: "error",
            notificationMessage: "",
            createCalledDialogOpen: false,

            appName: Api != null ? Api.getAppName() : "CMTECH - Mexx 2DO",
            logoimagesrc: "",
            faqs: [],
            filteredFaqs: [],
            subjectList: [],
            selectedSubject: "all",
            sortBy: "date",
            sortOrder: "desc",
            expandedFaqs: [], // Alterado para array
            highlightedFaqId: null,
            totalPages: 0,
            page: 1,
            pageStart: 0,
            pageEnd: 0,
            count: 0,
            searchValue: "",

            searchValueBar: "",
            hirerid: null,
            defaultExpanded: generalSearchFaqId ?? 0,
        }

        this.handleChange = this.handleChange.bind(this)
        this.openCreateCalledDialog = this.openCreateCalledDialog.bind(this)
        this.closeNotification = this.closeNotification.bind(this)
        this.searchArticle = this.searchArticle.bind(this)
        this.handleSearch = this.handleSearch.bind(this)
        this.handleSubjectChange = this.handleSubjectChange.bind(this)
        this.handleSearchChange = this.handleSearchChange.bind(this)
        this.handleSortChange = this.handleSortChange.bind(this)
        this.handleSortOrderChange = this.handleSortOrderChange.bind(this)
        this.getFaqs = this.getFaqs.bind(this)
        // this.filterFaqs = this.filterFaqs.bind(this)
        this.handleAttachmentClick = this.handleAttachmentClick.bind(this)
        this.getTagVariant = this.getTagVariant.bind(this)
        this.handleFaqClick = this.handleFaqClick.bind(this)
        this.checkForFaqId = this.checkForFaqId.bind(this)
        this.searchArticle = this.searchArticle.bind(this)

    }

    useStyles = () => {
        return withStyles({
            ImageList: {
                width: "100%",
                height: 100,
            },
        })
    }

    async componentDidMount() {

        PapiroConsole.log("entrei aqui 123")
        document.title = this.state.appName
        this.setState({ loading: true })
        const hirerid = await this.getHirerId()
        if (hirerid) {
            await this.getClientPortal(hirerid)
            await this.getSubjects(hirerid)

            const urlParams = new URLSearchParams(window.location.search)
            const faqId = urlParams.get("id")

            PapiroConsole.log("faqName")
            PapiroConsole.log("faqId")
            PapiroConsole.log(faqId)
            
            await this.getFaqs(1, hirerid, faqId)


        }
       
        // Scroll para o FAQ destacado após o carregamento
        this.checkForFaqId()

    }

    // async componentDidMount() {
    //     document.title = this.state.appName
    //     this.setState({ loading: true })
    //     const hirerid = await this.getHirerId()

    //     PapiroConsole.log("hirerid")
    //     PapiroConsole.log(hirerid)

    //     if (hirerid) {
    //         await this.getClientPortal()
    //         await this.getSubjects(hirerid)
    //         await this.getFaqs(hirerid)
    //     }
    //     // await this.getClientPortal()
    //     // await this.getSubjects()
    //     // await this.getFaqs()
    //     this.checkForFaqId()

    // }

    checkForFaqId() {
        const urlParams = new URLSearchParams(window.location.search)
        const faqId = urlParams.get("id")
        if (faqId) {
            this.setState((prevState) => ({
                highlightedFaqId: Number.parseInt(faqId),
                expandedFaqs: [...prevState.expandedFaqs, Number.parseInt(faqId)],
            }))
            setTimeout(() => {
                const element = document.getElementById(`faq-${faqId}`)
                if (element) {
                    element.scrollIntoView({ behavior: "smooth", block: "center" })
                }
            }, 500)
        }
    }

    handleFaqClick(faqId) {
  
        // const url = new URL(window.location)
        // url.searchParams.set("id", faqId)
        // window.history.pushState({}, "", url)

        this.setState((prevState) => {
            const isExpanded = prevState.expandedFaqs.includes(faqId)
            return {
                // highlightedFaqId: faqId,
                expandedFaqs: isExpanded
                    ? prevState.expandedFaqs.filter((id) => id !== faqId)
                    : [...prevState.expandedFaqs, faqId],
            }
        })
    }

    onChangePaginate = (e, page) => {
        this.setState({ page: page });
        this.getFaqs(page, this.state.hirerid);
    };

    setPagination(count, page) {
        let totalPage = Math.ceil(count / 10);
        this.setState({ totalPages: totalPage });
        if (count > 0) {
            if (page == 1) {
                if (count < 10) {
                    this.setState({ pageStart: 1, pageEnd: count });
                } else {
                    this.setState({ pageStart: 1, pageEnd: 10 });
                }
            } else {
                let pageStart = (page - 1) * 10 + 1;

                if (count >= page * 10) {
                    this.setState({ pageStart: pageStart, pageEnd: page * 10 });
                } else {
                    this.setState({ pageStart: pageStart, pageEnd: count });
                }
            }
        } else {
            this.setState({ totalPages: 0, page: 1, count: 0 });
        }
    }

    getTagColor = (tagName) => {
        // Cria um hash simples baseado no nome da tag
        let hash = 0
        for (let i = 0; i < tagName.length; i++) {
            const char = tagName.charCodeAt(i)
            hash = (hash << 5) - hash + char
            hash = hash & hash // Converte para 32bit integer
        }

        // Usa o hash para selecionar uma cor da paleta
        const colorIndex = Math.abs(hash) % TAG_COLOR_PALETTE.length
        return TAG_COLOR_PALETTE[colorIndex]
    }


    async getSubjects(hirerid) {
        this.setState({ loading: true })

        const url = `faq-subject/get-subjects?hirerid=${hirerid}`

        var result = await Api.get(url)
            .then((rs) => rs.data)
            .catch((err) => err)

        if (result.success) {
            this.setState({ subjectList: result.data })
        } else {
            const intl = this.props.intl
            this.setState({
                loading: false,
                openNotification: true,
                notificationVariant: "error",
                notificationMessage:
                    result.data &&
                        result.data.response &&
                        result.data.response.data &&
                        result.data.response.data.errors &&
                        result.data.response.data.errors[0]
                        ? result.data.response.data.errors[0]
                        : intl.formatMessage({ id: "process.error" }),
            })
            Api.kickoff(result)
        }
    }

    // filterFaqs() {
    //     const { faqs, selectedSubject, searchValue, sortBy, sortOrder } = this.state
    //     let filtered = [...faqs]

    //     // Filtrar por assunto
    //     if (selectedSubject !== "all") {
    //         filtered = filtered.filter((faq) => faq.subject.id.toString() === selectedSubject)
    //     }

    //     // Filtrar por busca
    //     if (searchValue && searchValue.trim()) {
    //         filtered = filtered.filter(
    //             (faq) =>
    //                 faq.question.toLowerCase().includes(searchValue.toLowerCase()) ||
    //                 faq.answer.toLowerCase().includes(searchValue.toLowerCase()) ||
    //                 (faq.faqtags && faq.faqtags.some((tag) => tag.tagname.toLowerCase().includes(searchValue.toLowerCase()))),
    //         )
    //     }

    //     // Ordenação
    //     filtered.sort((a, b) => {
    //         let comparison = 0

    //         if (sortBy === "date") {
    //             const dateA = new Date(a.createdat || a.id)
    //             const dateB = new Date(b.createdat || b.id)
    //             comparison = dateA - dateB
    //         } else if (sortBy === "name") {
    //             comparison = a.question.localeCompare(b.question)
    //         }

    //         return sortOrder === "asc" ? comparison : -comparison
    //     })

    //     this.setState({ filteredFaqs: filtered })
    // }

    getTagVariant() {
        const variants = ["primary", "secondary", "warning", "info"]
        const randomIndex = Math.floor(Math.random() * variants.length)
        return variants[randomIndex]
    }

    handleSearch() {
        // this.filterFaqs()
    }

    handleSubjectChange(event) {
        const value = event.target.value;
        // Captura o id do assunto selecionado
        const selectedId = value === "all" ? "all" : Number(value);
        this.setState({ selectedSubject: selectedId }, () => {
            // Se quiser usar o id imediatamente, pode acessar selectedId aqui
            // Exemplo: console.log('Assunto selecionado id:', selectedId);
        });
    }

    handleSearchChange(event) {
        this.setState({ searchValue: event.target.value })
    }

    handleSortChange(event) {
        this.setState({ sortBy: event.target.value }, () => {
            // this.filterFaqs()
        })
    }

    handleSortOrderChange(event) {
        this.setState({ sortOrder: event.target.value }, () => {
            // this.filterFaqs()
        })
    }

    handleAttachmentClick(attachment) {
        console.log("Abrindo anexo:", attachment)
    }

    getHirerId = async () => {
        const intl = this.props.intl

        return await Api.get(`/configs/cancreateclientusersexternal`)
            .then((result) => {
                if (result.data.success) {
                    if (result.data.data != null) {
                        if (result.data.data.hirerid != null && result.data.data.hirerid > 0) {
                            this.setState({ hirerid: result.data.data.hirerid })
                            return result.data.data.hirerid
                        } else {
                            var l = this.props.match.params.lang
                            document.location.href = `/${l}/unavailable`
                            return null
                        }
                    }
                } else {
                    this.setState({
                        loading: false,
                        openNotification: true,
                        notificationVariant: "error",
                        notificationMessage:
                            result.data &&
                                result.data.response &&
                                result.data.response.data &&
                                result.data.response.data.errors &&
                                result.data.response.data.errors[0]
                                ? result.data.response.data.errors[0]
                                : intl.formatMessage({ id: "process.error" }),
                    })
                    Api.kickoff(result)
                    return null
                }
            })
            .catch((err) => {
                this.setState({
                    loading: false,
                    openNotification: true,
                    notificationVariant: "error",
                    notificationMessage:
                        err.response && err.response.data && err.response.data.errors && err.response.data.errors[0]
                            ? err.response.data.errors[0]
                            : intl.formatMessage({ id: "process.error" }),
                })
                Api.kickoff(err)
                return null
            })
    }

    async getFaqs(page = 1, hirerid = null, faqId) {

        // Construir parâmetros da query
        this.setState({ loading: true });
        const { searchValue, selectedSubject, sortBy, sortOrder } = this.state



        const params = new URLSearchParams()
        params.append("page", page)
        params.append("hirerId", hirerid)
        if (selectedSubject !== "all") {
            params.append("subjectid", selectedSubject)
        }
        if (searchValue && searchValue.trim()) {
            params.append("search", searchValue.trim())
        }
        if(faqId){
            params.append("faqid", faqId)
        }

        PapiroConsole.log("sortBy")
        PapiroConsole.log(sortBy)
        PapiroConsole.log("sortOrder")
        PapiroConsole.log(sortOrder)
        params.append("sortBy", sortBy)
        params.append("sortOrder", sortOrder)


        const queryString = params.toString()


        const url = `faq/paginate-external${queryString ? `?${queryString}` : ""}`
        Api.get(url)

            .then((result) => {
                PapiroConsole.log("Result do getItems");
                PapiroConsole.log(result);
                if (result.data.success) {


                    PapiroConsole.log("result aqui")
                    PapiroConsole.log(result)
                    let count = result.data.count;
                    this.setState({ count: count, loading: false });
                    this.setPagination(count, page);

                    var faqs = result && result.data && result.data.data ? result.data.data : []

                    this.setState({
                        faqs: faqs,
                        filteredFaqs: faqs,


                    })
                } else {
                    const intl = this.props.intl;
                    this.setState({
                        loading: false,
                        openNotification: true,
                        notificationVariant: "error",
                        notificationMessage:
                            result.data &&
                                result.data.response &&
                                result.data.response.data &&
                                result.data.response.data.errors &&
                                result.data.response.data.errors[0]
                                ? result.data.response.data.errors[0]
                                : intl.formatMessage({ id: "process.error" }),
                    });
                    Api.kickoff(result);
                }
            })
            .catch((err) => {
                const intl = this.props.intl;
                this.setState({
                    loading: false,
                    openNotification: true,
                    notificationVariant: "error",
                    notificationMessage:
                        err.response &&
                            err.response.data &&
                            err.response.data.errors &&
                            err.response.data.errors[0]
                            ? err.response.data.errors[0]
                            : intl.formatMessage({ id: "process.error" }),
                });
                Api.kickoff(err);
            });

    }

    async getClientPortal(hirerid) {
        const intl = this.props.intl

        await Api.get(`/clientportal/external/hirer/${hirerid}`)
            .then((result) => {
                if (result.data && result.data.success) {
                    this.setState({
                        clientportal: result.data.data,
                    })
                    if (result.data.data.logoimage) {
                        this.getFile(result.data.data.logoimage)
                    } else {
                        const image = Api.getLoginImage()
                        if (image) {
                            this.getFile(image)
                        } else {
                            this.setState({ logoimagesrc: LogoMarca })
                        }
                    }
                } else {
                    this.setState({
                        loading: false,
                        openNotification: true,
                        notificationVariant: "error",
                        notificationMessage:
                            result.data &&
                                result.data.response &&
                                result.data.response.data &&
                                result.data.response.data.errors &&
                                result.data.response.data.errors[0]
                                ? result.data.response.data.errors[0]
                                : intl.formatMessage({ id: "process.error" }),
                    })
                    Api.kickoff(result.data)
                }
            })
            .catch((err) => {
                this.setState({
                    loading: false,
                    openNotification: true,
                    notificationVariant: "error",
                    notificationMessage:
                        err.response && err.response.data && err.response.data.errors && err.response.data.errors[0]
                            ? err.response.data.errors[0]
                            : intl.formatMessage({ id: "process.error" }),
                })
                Api.kickoff(err)
            })
    }

    getFile = async (image) => {
        const xhttpImage = new XMLHttpRequest()
        xhttpImage.open("GET", `${image.anonymoustoken}`, false)
        xhttpImage.setRequestHeader("Access-Control-Allow-Origin", "*")
        try {
            xhttpImage.send()
            const ImageData = JSON.parse(xhttpImage.responseText)
            if (ImageData && ImageData.data && ImageData.data.length > 0) {
                this.setState({ logoimagesrc: ImageData.data })
            }
        } catch (e) {
            PapiroConsole.log("e")
            PapiroConsole.log(e)
        }
    }

    getFileAttachment = async (attachment) => {
        const intl = this.props.intl
        this.setState({ loading: true })

        const url = await getFileDownloadLink(attachment.attachmentid)
        if (url && url.data && url.data.data && url.data.success) {
            this.setState({ loading: false })
            window.open(`${url.data.data}`, "_blank")
        } else {
            this.setState({
                loading: false,
                openNotification: true,
                notificationVariant: "error",
                notificationMessage: "Ocorreu um erro ao buscar o arquivo solicitado",
            })
        }
    }

    openCreateCalledDialog = (calledServiceSettingsList) => {
        this.setState({
            createCalledDialogOpen: true,
            calledServiceSettingsList: calledServiceSettingsList,
        })
    }

    closeNotification = () => {
        this.setState({ openNotification: false })
    }

    handleChange = (e) => {
        const name = e.target.name
        const value = e.target.value

        this.setState({
            [name]: value,
        })
    }

    searchArticle = () => {
        this.props.addCurrentSearch(this.state.searchValueBar, "text")
        this.props.history.push(`/${this.props.match.params.lang}/article-list-external`)
    }

    render() {
        const { classes, intl } = this.props
        const {
            filteredFaqs,
            subjectList,
            selectedSubject,
            searchValue,
            sortBy,
            sortOrder,
            loading,
            clientportal,
            logoimagesrc,
            expandedFaqs,
            highlightedFaqId,
            defaultExpanded 
        } = this.state

        const classes2 = this.useStyles()

        if (!clientportal.enablefaq) {
            return (
                <Box
                    sx={{
                        minHeight: "100vh",
                        width: "100vw",
                        backgroundColor: "#f8fafc",
                        display: "flex",
                        flexDirection: "column",
                        margin: 0,
                        padding: 0,
                        boxSizing: "border-box",
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                    }}
                >

                    <Header
                        intl={this.props.intl}
                        clientportal={clientportal}
                        calledservicesettings={clientportal.calledservicesettingsclientportals}
                        language={this.props.match.params.lang}
                        openCreateCalledDialog={this.openCreateCalledDialog}
                        logoimagesrc={logoimagesrc}
                    />
                    <Footer intl={intl} clientportal={clientportal} setLoading={(loading) => this.setState({ loading: loading })} />
                    {this.state.loading && (
                        <Overlay>
                            <CircularProgress color="secondary" />
                        </Overlay>
                    )}

                    <CustomizedSnackbars
                        variant={this.state.notificationVariant}
                        message={this.state.notificationMessage}
                        isOpen={this.state.openNotification}
                        toClose={this.closeNotification}
                    />
                </Box>

            )
        }

        return (
            <Box
                sx={{
                    minHeight: "100vh",
                    width: "100vw",
                    backgroundColor: "#f8fafc",
                    display: "flex",
                    flexDirection: "column",
                    margin: 0,
                    padding: 0,
                    boxSizing: "border-box",
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    overflow: "auto", // Garante que a rolagem aconteça dentro do Box e o fundo permaneça
                }}
            >
                <Header
                    intl={this.props.intl}
                    clientportal={clientportal}
                    calledservicesettings={clientportal.calledservicesettingsclientportals}
                    language={this.props.match.params.lang}
                    openCreateCalledDialog={this.openCreateCalledDialog}
                    logoimagesrc={logoimagesrc}
                />

                {/* Seção Hero simplificada */}
                <Box
                    sx={{
                        backgroundColor: clientportal.defaultcolor,

                    }}
                >
                    {
                        clientportal.mainimage ?
                            <div id="mainimage" style={{ display: "block", width: "100vw", height: "300px", textAlign: "center", alignContent: "center" }}>
                                {
                                    clientportal.enablesearch &&
                                    <>
                                        <h2 style={{ color: "white", marginBottom: "10px", fontWeight: "bold", fontSize: "25px" }}>
                                            {clientportal.searchtext}
                                        </h2>
                                        <StyledTextField
                                            variant="outlined"
                                            name="searchValueBar"
                                            className={classes.root}
                                            label={intl.formatMessage({ id: "search" })}
                                            value={this.state.searchValueBar}
                                            placeholder={intl.formatMessage({ id: "search.news.placeholder" })}
                                            onChange={this.handleChange}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    this.searchArticle();
                                                }
                                            }}
                                            color="primary"
                                            sx={{
                                                width: "600px"
                                            }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={_ => this.searchArticle()}>
                                                            <Search />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        >
                                        </StyledTextField>
                                    </>
                                }
                            </div>
                            :
                            <div style={{ display: "block", width: "101vw", height: "300px", backgroundColor: clientportal.defaultcolor, textAlign: "center", alignContent: "center" }}>
                                {
                                    clientportal.enablesearch &&
                                    <>
                                        <h2 style={{ color: "white", marginBottom: "10px", fontWeight: "bold", fontSize: "25px" }}>
                                            {clientportal.searchtext}
                                        </h2>
                                        <StyledTextField
                                            variant="outlined"
                                            name="searchValueBar"
                                            label={intl.formatMessage({ id: "search" })}
                                            value={this.state.searchValueBar}
                                            placeholder={intl.formatMessage({ id: "search.news.placeholder" })}
                                            onChange={this.handleChange}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    this.searchArticle();
                                                }
                                            }}
                                            color="primary"
                                            sx={{ width: "600px", backgroundColor: "#fff" }}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={_ => this.searchArticle()}>
                                                            <Search />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </>
                                }
                            </div>
                    }
                </Box>

                {/* Conteúdo principal */}
                <Container maxWidth="lg" sx={{ py: 4, px: 3, flex: 1, display: "flex", flexDirection: "column" }}>
                    {/* Seção de filtros e busca */}
                    <MainCard sx={{ mb: 3 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Grid container spacing={2} alignItems="center">
                                {/* Campo de busca */}
                                <Grid item xs={12} md={4}>
                                    <StyledTextField
                                        fullWidth
                                        placeholder="Buscar por perguntas ou respostas"
                                        value={searchValue}
                                        onChange={this.handleSearchChange}
                                        variant="outlined"
                                        size="small"
                                    />
                                </Grid>

                                {/* Filtro por assunto */}
                                <Grid item xs={12} md={2}>
                                    <StyledFormControl fullWidth size="small">
                                        <InputLabel id="subject-select-label">Assunto</InputLabel>
                                        <Select
                                            labelId="subject-select-label"
                                            id="subject-select"
                                            value={selectedSubject}
                                            onChange={this.handleSubjectChange}
                                            label="Assunto"
                                        >
                                            <MenuItem value="all">- Qualquer -</MenuItem>
                                            {subjectList.map((subject) => (
                                                <MenuItem key={subject.id} value={subject.id.toString()}>
                                                    {subject.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </StyledFormControl>
                                </Grid>

                                {/* Ordenação por */}
                                <Grid item xs={12} md={2}>
                                    <StyledFormControl fullWidth size="small">
                                        <InputLabel id="sort-select-label">Ordenar por</InputLabel>
                                        <Select
                                            labelId="sort-select-label"
                                            id="sort-select"
                                            value={sortBy}
                                            onChange={this.handleSortChange}
                                            label="Ordenar por"
                                        >
                                            <MenuItem value="date">Data</MenuItem>
                                            <MenuItem value="name">Nome</MenuItem>
                                        </Select>
                                    </StyledFormControl>
                                </Grid>

                                {/* Ordem */}
                                <Grid item xs={12} md={2}>
                                    <StyledFormControl fullWidth size="small">
                                        <InputLabel id="order-select-label">Ordem</InputLabel>
                                        <Select
                                            labelId="order-select-label"
                                            id="order-select"
                                            value={sortOrder}
                                            onChange={this.handleSortOrderChange}
                                            label="Ordem"
                                        >
                                            <MenuItem value="asc">
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <ArrowUpward fontSize="small" />
                                                    Crescente
                                                </Box>
                                            </MenuItem>
                                            <MenuItem value="desc">
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <ArrowDownward fontSize="small" />
                                                    Decrescente
                                                </Box>
                                            </MenuItem>
                                        </Select>
                                    </StyledFormControl>
                                </Grid>

                                {/* Botão de busca */}
                                <Grid item xs={12} md={2}>
                                    <SearchButton fullWidth onClick={() => this.getFaqs(1)} startIcon={<Search />} disabled={loading}>
                                        Buscar
                                    </SearchButton>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </MainCard>

                    {/* Lista de FAQs */}
                    {!loading && (
                        <MainCard>
                            <CardContent
                                sx={{
                                    p: 0,
                                    pb: 0,
                                    "&:last-child": {
                                        paddingBottom: 0,
                                    },
                                }}
                            >
                                {filteredFaqs.map((faq) => (
                                    <StyledAccordion
                                        key={faq.id}
                                        disableGutters
                                        // highlighted={highlightedFaqId === faq.id}
                                        // expanded={expandedFaqs.includes(faq.id)}
                                        onChange={() => this.handleFaqClick(faq.id)}
                                        id={`faq-${faq.id}`}
                                        defaultExpanded={faq.id == defaultExpanded}
                                    >
                                        <StyledAccordionSummary expandIcon={<ExpandMore />}>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 500,
                                                    color: "#111827",
                                                    fontSize: "0.95rem",
                                                    lineHeight: 1.5,
                                                }}
                                            >
                                                {faq.question}
                                            </Typography>
                                        </StyledAccordionSummary>
                                        <StyledAccordionDetails>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: "#4b5563",
                                                    lineHeight: 1.6,
                                                    fontSize: "0.9rem",
                                                    mb: 3,
                                                }}
                                                component="div"
                                                dangerouslySetInnerHTML={{ __html: faq.answer }}
                                            />

                                            {faq.faqtags && faq.faqtags.length > 0 && (
                                                <Box sx={{ mb: 3 }}>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: "#6b7280",
                                                            fontWeight: 600,
                                                            textTransform: "uppercase",
                                                            letterSpacing: "0.05em",
                                                            mb: 1,
                                                            display: "block",
                                                        }}
                                                    >
                                                        Tags
                                                    </Typography>
                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                        {faq.faqtags.map((tag) => {
                                                            const tagColor = this.getTagColor(tag.tagname)

                                                            return (
                                                                <Chip
                                                                    key={tag.id}
                                                                    label={tag.tagname}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{
                                                                        backgroundColor: tagColor.backgroundColor,
                                                                        color: tagColor.color,
                                                                        border: `1px solid ${tagColor.border}`,
                                                                        fontWeight: 500,
                                                                        fontSize: "0.75rem",
                                                                        height: "24px",
                                                                        "&:hover": {
                                                                            backgroundColor: tagColor.color,
                                                                            color: "#ffffff",
                                                                            transform: "translateY(-1px)",
                                                                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                                                        },
                                                                        transition: "all 0.2s ease-in-out",
                                                                        cursor: "pointer",
                                                                    }}
                                                                />
                                                            )
                                                        })}
                                                    </Box>
                                                </Box>
                                            )}

                                            {faq.faqattachments && faq.faqattachments.length > 0 && (
                                                <>
                                                    <Divider sx={{ my: 2 }} />
                                                    <Box>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: "#6b7280",
                                                                fontWeight: 600,
                                                                textTransform: "uppercase",
                                                                letterSpacing: "0.05em",
                                                                mb: 2,
                                                                display: "block",
                                                            }}
                                                        >
                                                            Anexos ({faq.faqattachments.length})
                                                        </Typography>
                                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                            {faq.faqattachments.map((attachment) => (
                                                                <AttachmentItem key={attachment.id} onClick={() => this.getFileAttachment(attachment)}>
                                                                    <AttachmentIcon filename={attachment.name} />
                                                                    <Box sx={{ ml: 1, flex: 1 }}>
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                fontSize: "0.8rem",
                                                                                fontWeight: 500,
                                                                                color: "#374151",
                                                                                lineHeight: 1.2,
                                                                            }}
                                                                        >
                                                                            {attachment.name}
                                                                        </Typography>
                                                                        <Typography
                                                                            variant="caption"
                                                                            sx={{
                                                                                color: "#6b7280",
                                                                                fontSize: "0.7rem",
                                                                            }}
                                                                        >
                                                                            {attachment.size}
                                                                        </Typography>
                                                                    </Box>
                                                                    <IconButton
                                                                        size="small"
                                                                        sx={{
                                                                            ml: 1,
                                                                            color: "#6366f1",
                                                                            "&:hover": {
                                                                                backgroundColor: "#e0e7ff",
                                                                            },
                                                                        }}
                                                                    >
                                                                        <OpenInNew fontSize="small" />
                                                                    </IconButton>
                                                                </AttachmentItem>
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                </>
                                            )}
                                        </StyledAccordionDetails>
                                    </StyledAccordion>
                                ))}

                                {filteredFaqs.length === 0 && !loading && (
                                    <Box sx={{ p: 6, textAlign: "center" }}>
                                        <Typography variant="body1" sx={{ color: "#6b7280" }}>
                                            Nenhum FAQ encontrado para os critérios selecionados.
                                        </Typography>
                                    </Box>
                                )}

                            </CardContent>
                        </MainCard>
                    )}
                    {filteredFaqs.length > 0 && !loading && (
                        <Grid container style={{ marginBottom: 12, marginTop: 8 }}>
                            <Grid item xs={12} md={3}>
                                <Pagination
                                    count={this.state.totalPages}
                                    page={this.state.page}
                                    onChange={this.onChangePaginate}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3} style={{ marginTop: 6 }}>
                                {this.state.pageStart} {<FormattedMessage id="to" />}{" "}
                                {this.state.pageEnd} {<FormattedMessage id="of" />}{" "}
                                {this.state.count} {<FormattedMessage id="register" />}
                            </Grid>
                        </Grid>
                    )}
                </Container>



                <Footer intl={intl} clientportal={clientportal} setLoading={(loading) => this.setState({ loading: loading })} />
                {this.state.loading && (
                    <Overlay>
                        <CircularProgress color="secondary" />
                    </Overlay>
                )}

                <CustomizedSnackbars
                    variant={this.state.notificationVariant}
                    message={this.state.notificationMessage}
                    isOpen={this.state.openNotification}
                    toClose={this.closeNotification}
                />


            </Box>
        )
    }
}

FaqExternal.propTypes = (state) => ({
    classes: PropTypes.object.isRequired,
})

const mapStateToProps = (state) => ({
    classes: PropTypes.object.isRequired,
})

// const mapDispatchToProps = (dispatch) => bindActionCreators(articleActions, dispatch)

// export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(withStyles(Styles)(FaqExternal)))
// FaqExternal.contextType = AbilityContext


const mapDispatchToProps = dispatch =>
    bindActionCreators(articleActions, dispatch);
export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(withStyles(Styles)(FaqExternal)));
FaqExternal.contextType = AbilityContext;